import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';

const COLORS = [
  '#378ADD', '#1D9E75', '#D85A30', '#7F77DD',
  '#D4537E', '#BA7517', '#0F6E56', '#993C1D',
  '#534AB7', '#639922',
];

export default function OrderPanel({ order, setOrder }) {
  const fileRef = useRef();
  const [draft, setDraft] = useState({ w: '', h: '', qty: '' });

  function updateRow(index, field, val) {
    setOrder(order.map((o, i) => (i === index ? { ...o, [field]: val } : o)));
  }

  function commitRow(index, field, raw) {
    const num = field === 'qty' ? parseInt(raw) : parseFloat(raw);
    if (!num || num <= 0) { setOrder([...order]); return; }
    setOrder(order.map((o, i) => (i === index ? { ...o, [field]: num } : o)));
  }

  function removeRow(index) {
    setOrder(order.filter((_, i) => i !== index));
  }

  function commitDraft() {
    const fw = parseFloat(draft.w);
    const fh = parseFloat(draft.h);
    const iq = parseInt(draft.qty);
    if (!fw || !fh || !iq || fw <= 0 || fh <= 0 || iq <= 0) return;
    const existing = order.find((o) => o.w === fw && o.h === fh);
    if (existing) {
      setOrder(order.map((o) => (o.w === fw && o.h === fh ? { ...o, qty: o.qty + iq } : o)));
    } else {
      setOrder([...order, { w: fw, h: fh, qty: iq }]);
    }
    setDraft({ w: '', h: '', qty: '' });
  }

  function handleDraftKey(e, nextId) {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (nextId) {
        e.preventDefault();
        document.getElementById(nextId)?.focus();
      } else {
        commitDraft();
      }
    }
  }

  function exportExcel() {
    const rows = order.map((o) => ({ W: o.w, H: o.h, Qty: o.qty }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Order');
    XLSX.writeFile(wb, 'order.xlsx');
  }

  function parseAndMerge(items) {
    let next = [...order];
    for (const item of items) {
      const idx = next.findIndex((o) => o.w === item.w && o.h === item.h);
      if (idx >= 0) next[idx] = { ...next[idx], qty: next[idx].qty + item.qty };
      else next.push(item);
    }
    setOrder(next);
  }

  function importExcel(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const items = rows
        .filter((r) => !isNaN(parseFloat(r[0])))
        .map((r) => ({ w: parseFloat(r[0]), h: parseFloat(r[1]), qty: parseInt(r[2]) }))
        .filter((r) => r.w > 0 && r.h > 0 && r.qty > 0);
      parseAndMerge(items);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const items = text.trim().split('\n')
      .map((line) => line.split('\t').map((v) => v.trim()))
      .filter((r) => !isNaN(parseFloat(r[0])))
      .map((r) => ({ w: parseFloat(r[0]), h: parseFloat(r[1]), qty: parseInt(r[2]) }))
      .filter((r) => r.w > 0 && r.h > 0 && r.qty > 0);
    if (items.length > 0) {
      e.preventDefault();
      parseAndMerge(items);
    }
  }

  const totalPieces = order.reduce((s, o) => s + o.qty, 0);
  const totalArea = order.reduce((s, o) => s + o.w * o.h * o.qty, 0);

  return (
    <div className="panel" onPaste={handlePaste}>
      <div className="panel-header-row">
        <div>
          <h2 className="panel-title">Order</h2>
          <p className="panel-desc">Pieces to cut from stock</p>
        </div>
        <div className="panel-actions">
          <button className="btn-xl-action btn-xl-danger" onClick={() => setOrder([])} title="Clear all pieces" disabled={order.length === 0}>Clear all</button>
          <button className="btn-xl-action" onClick={exportExcel} title="Export to Excel">↓ Excel</button>
          <button className="btn-xl-action" onClick={() => fileRef.current.click()} title="Import Excel file">↑ Import</button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={importExcel} />
        </div>
      </div>

      <div className="xl-table-wrap">
        <table className="xl-table">
          <thead>
            <tr>
              <th></th>
              <th>W (cm)</th>
              <th>H (cm)</th>
              <th>Qty</th>
              <th>Area (cm²)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {order.map((o, i) => {
              const color = COLORS[i % COLORS.length];
              return (
                <tr key={i} className="xl-row">
                  <td>
                    <span className="xl-color-dot" style={{ background: color }} />
                  </td>
                  <td>
                    <input
                      className="xl-cell-input"
                      type="number"
                      value={o.w}
                      min="0.1"
                      step="0.5"
                      onChange={(e) => updateRow(i, 'w', e.target.value)}
                      onBlur={(e) => commitRow(i, 'w', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="xl-cell-input"
                      type="number"
                      value={o.h}
                      min="0.1"
                      step="0.5"
                      onChange={(e) => updateRow(i, 'h', e.target.value)}
                      onBlur={(e) => commitRow(i, 'h', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="xl-cell-input"
                      type="number"
                      value={o.qty}
                      min="1"
                      onChange={(e) => updateRow(i, 'qty', e.target.value)}
                      onBlur={(e) => commitRow(i, 'qty', e.target.value)}
                    />
                  </td>
                  <td className="xl-cell-ro">{(o.w * o.h * o.qty).toFixed(1)}</td>
                  <td><button className="btn-remove" onClick={() => removeRow(i)}>×</button></td>
                </tr>
              );
            })}

            {/* Draft / new row */}
            <tr className="xl-row xl-draft-row">
              <td><span className="xl-color-dot" style={{ background: '#ccc' }} /></td>
              <td>
                <input
                  id="order-draft-w"
                  className="xl-cell-input xl-draft"
                  type="number"
                  value={draft.w}
                  min="0.1"
                  step="0.5"
                  placeholder="W"
                  onChange={(e) => setDraft({ ...draft, w: e.target.value })}
                  onKeyDown={(e) => handleDraftKey(e, 'order-draft-h')}
                />
              </td>
              <td>
                <input
                  id="order-draft-h"
                  className="xl-cell-input xl-draft"
                  type="number"
                  value={draft.h}
                  min="0.1"
                  step="0.5"
                  placeholder="H"
                  onChange={(e) => setDraft({ ...draft, h: e.target.value })}
                  onKeyDown={(e) => handleDraftKey(e, 'order-draft-qty')}
                />
              </td>
              <td>
                <input
                  id="order-draft-qty"
                  className="xl-cell-input xl-draft"
                  type="number"
                  value={draft.qty}
                  min="1"
                  placeholder="Qty"
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                  onKeyDown={(e) => handleDraftKey(e, null)}
                  onBlur={commitDraft}
                />
              </td>
              <td className="xl-cell-ro" style={{ color: 'var(--text-muted)' }}>
                {draft.w && draft.h && draft.qty
                  ? (parseFloat(draft.w) * parseFloat(draft.h) * parseInt(draft.qty) || 0).toFixed(1)
                  : '—'}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {order.length > 0 && (
        <div className="xl-footer">
          <span>{totalPieces} pieces</span>
          <span>{totalArea.toFixed(0)} cm² total</span>
        </div>
      )}
    </div>
  );
}
